import _ from "lodash";
import moment from "moment";
import historyProvider from "./historyProvider.js";
import store from "../../../store.js";

// Global subscription object to track current chart subscription
let subscription = {};

/**
 * Stream Provider for TradingView chart
 * Uses the singleton WebSocket instance from WebSocketContext
 * Manages real-time candle data updates
 */
const streamProvider = {
  /**
   * Subscribe to bars (candles) for a trading pair
   * Called by TradingView when chart is initialized or symbol changes
   *
   * @param {object} symbolInfo - Symbol information from TradingView
   * @param {string} resolution - Chart resolution (1, 5, 15, 30, etc.)
   * @param {function} updateCb - Callback to update chart with new bar data
   * @param {string} uid - Unique identifier for this subscription
   * @param {function} resetCache - Function to reset chart cache
   */
  subscribeBars: (symbolInfo, resolution, updateCb, uid, resetCache) => {
    // Extract base and quote currencies from symbol name (e.g., "BTC/USDT")
    const [baseCurrency, quoteCurrency] = symbolInfo.name.split("/");

    // Get last bar from history or create default
    const lastBar = _.get(
      historyProvider,
      `history.${symbolInfo.name}.lastBar`,
      {
        time: moment().startOf("m").valueOf(), // Start of current minute
        open: 0,
        close: 0,
        high: 0,
        low: 0,
        volume: 0,
      }
    );

    // Create subscription object with all necessary data
    subscription = {
      channel: `CH.${baseCurrency}_${quoteCurrency}.${resolution}`, // WebSocket event name
      uid, // Unique subscription ID
      resolution, // Chart resolution
      symbolInfo, // Symbol information
      listener: updateCb, // Callback to update chart
      lastBar, // Last bar data
      baseCurrency, // Base currency
      quoteCurrency, // Quote currency
    };

    // Set up periodic price updates from Redux store
    // This ensures the chart updates even between candle intervals
    const priceUpdateInterval = setInterval(() => {
      const { latestTradePrice } = store.getState().exchange.tradingPairStats;
      // Update current bar with latest price
      if (latestTradePrice) {
        updatePrice(latestTradePrice);
      }
    }, 5000); // Update every 5 seconds

    // Store interval ID for cleanup
    subscription.priceUpdateInterval = priceUpdateInterval;
  },

  /**
   * Unsubscribe from bars
   * Called when chart is destroyed or symbol changes
   */
  unsubscribeBars: () => {
    // Clear price update interval
    if (subscription.priceUpdateInterval) {
      clearInterval(subscription.priceUpdateInterval);
    }

    // Clear subscription object
    subscription = {};
  },
};

/**
 * Handle incoming candle data from WebSocket
 * This should be called from your WebSocket message handler
 *
 * @param {object} barData - Candle data from WebSocket
 */
export const handleCandleData = (barData) => {
  // Ensure there's an active subscription
  if (_.isEmpty(subscription)) {
    return;
  }

  // Validate incoming data
  if (!barData || !barData.time) {
    return;
  }

  // Ignore old data (data older than last bar)
  if (barData.time < subscription.lastBar.time) {
    return;
  }

  // Update bar with new data
  const lastBar = updateBar(barData, subscription);

  // Notify TradingView chart of update
  subscription.listener(lastBar);

  // Update our record of last bar
  subscription.lastBar = lastBar;
};

/**
 * Update current bar's volume
 * Called when a new trade occurs
 *
 * @param {object} ticker - Trade data containing volume
 */
export const updateVolume = (ticker) => {
  if (!_.isEmpty(subscription)) {
    let lastBar = subscription.lastBar;
    // Add trade volume to current bar
    lastBar.volume += ticker.Volume;
    // Notify chart of update
    subscription.listener(lastBar);
  }
};

/**
 * Update current bar's price (high, low, close)
 * Called periodically or when new trade occurs
 *
 * @param {number} price - New price to update
 */
export const updatePrice = (price) => {
  if (!_.isEmpty(subscription)) {
    let lastBar = subscription.lastBar;

    // Only update if price is valid
    if (price && price !== 0) {
      // Update low if price is lower
      if (price < lastBar.low) {
        lastBar.low = price;
      }
      // Update high if price is higher
      else if (price > lastBar.high) {
        lastBar.high = price;
      }

      // Always update close with latest price
      lastBar.close = price;

      // Notify chart of update
      subscription.listener(lastBar);
    }
  }
};

/**
 * Update bar with new candle data
 * Handles both new bars and updates to current bar
 *
 * @param {object} barData - New candle data
 * @param {object} subscription - Current subscription info
 * @returns {object} - Updated bar data
 */
const updateBar = (barData, subscription) => {
  let lastBar = subscription.lastBar;
  let resolution = subscription.resolution;

  // Convert resolution to minutes for calculation
  if (resolution.includes("D")) {
    resolution = 1440; // 1 day in minutes
  } else if (resolution.includes("W")) {
    resolution = 10080; // 1 week in minutes
  }

  // Calculate milliseconds per candle
  let coeff = resolution * 60 * 1000;

  // Check if new data belongs to same candle interval
  let isSameInterval =
    Math.floor(barData.time / coeff) === Math.floor(lastBar.time / coeff);

  let _lastBar;

  if (!isSameInterval) {
    // New candle interval started - create new bar
    _lastBar = {
      time: barData.time, // New bar time
      open: lastBar.close, // New bar opens at previous close
      high: lastBar.close, // Initial high
      low: lastBar.close, // Initial low
      close: barData.close, // New close price
      volume: barData.volume, // New volume
    };
  } else {
    // Same interval - update existing bar

    // Update low if new price is lower
    if (barData.close < lastBar.low) {
      lastBar.low = barData.close;
    }
    // Update high if new price is higher
    else if (barData.close > lastBar.high) {
      lastBar.high = barData.close;
    }

    // Add new volume to existing volume
    lastBar.volume += barData.volume;

    // Update close price
    lastBar.close = barData.close;

    _lastBar = lastBar;
  }

  return _lastBar;
};

/**
 * Get current subscription info
 * Useful for debugging or external access
 *
 * @returns {object} - Current subscription object
 */
export const getSubscription = () => subscription;

export default streamProvider;
